import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetches a remote asset and exposes it as a `blob:` object URL.
 *
 * Use this for any asset hosted on an origin the app's CSP allows in
 * `connect-src` but NOT in `img-src` / `media-src` — GCS signed URLs
 * (`https://storage.googleapis.com`) being the common case. Pointing an
 * `<img src>` straight at such a URL is blocked by the policy, while fetching
 * the bytes and rendering the resulting `blob:` URL is allowed.
 *
 * The object URL is revoked when the blob changes or the component unmounts.
 *
 * @param url        Asset URL, or null/undefined to skip the fetch.
 * @param queryScope Cache-key namespace, so callers with different lifetimes
 *                   don't share entries.
 */
export function useFetchedObjectUrl(
  url: string | null | undefined,
  queryScope = 'opal:object-url',
  { enabled = true, staleTime = 5 * 60 * 1000 }: { enabled?: boolean; staleTime?: number } = {}
) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const query = useQuery({
    queryKey: [queryScope, url],
    queryFn: async () => {
      // No auth headers: signed URLs carry their own credentials in the query
      // string, and adding an Authorization header can break the signature.
      const response = await fetch(url as string);
      if (!response.ok) {
        console.error('Failed to fetch remote asset for object URL', {
          queryScope,
          status: response.status,
          statusText: response.statusText,
        });
        throw new Error(`Failed to fetch remote asset: ${response.status}`);
      }
      return response.blob();
    },
    enabled: enabled && !!url,
    retry: false,
    staleTime,
  });

  const blob = query.data;
  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const created = URL.createObjectURL(blob);
    setObjectUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [blob]);

  return { ...query, objectUrl };
}
